
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /** Navigate to home page and scroll to section */
  const navigateToSection = (sectionId: string) => {
    if (location.pathname === '/') {
      // If already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other page, navigate to home first then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  /** Scroll smoothly to a page section identified by its DOM id. */
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /** Duplicate of scrollToSection – kept for backward‑compatibility. */
  const handleNavClick = (sectionId: string) => {
    navigateToSection(sectionId);
  };

  /** Change i18n language with minimal error handling. */
  const changeLanguage = (lng: string) => {
    try {
      i18n.changeLanguage(lng);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLanguageOpen(false);
    }
  };

  return (
    <nav className="fixed w-full top-0 left-0 z-50 px-6 py-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white/10 backdrop-blur-md rounded-full border border-white/10 shadow-lg shadow-black/5 px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center group cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="https://static.readdy.ai/image/14090482ad00f674843640af3b243289/9c8773d1b1b036c04dea96b1d431535f.png"
              alt="Flidge Logo"
              className="h-8 w-auto transition-all duration-300 group-hover:scale-110"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => navigateToSection('hero')}
              className="text-white hover:text-indigo-400 transition-all duration-300 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-0 after:bg-indigo-400 hover:after:w-full after:transition-all after:duration-300"
            >
              {t('home')}
            </button>
            <button
              onClick={() => navigateToSection('features')}
              className="text-white hover:text-indigo-400 transition-all duration-300 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-0 after:bg-indigo-400 hover:after:w-full after:transition-all after:duration-300"
            >
              {t('features')}
            </button>
            <button
              onClick={() => navigateToSection('cases')}
              className="text-white hover:text-indigo-400 transition-all duration-300 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-0 after:bg-indigo-400 hover:after:w-full after:transition-all after:duration-300"
            >
              {t('cases')}
            </button>
            <button
              onClick={() => navigateToSection('pricing')}
              className="text-white hover:text-indigo-400 transition-all duration-300 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-0 after:bg-indigo-400 hover:after:w-full after:transition-all after:duration-300"
            >
              {t('pricing')}
            </button>
            <button
              onClick={() => navigateToSection('about')}
              className="text-white hover:text-indigo-400 transition-all duration-300 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-0 after:bg-indigo-400 hover:after:w-full after:transition-all after:duration-300"
            >
              {t('about')}
            </button>
          </div>

          {/* CTA and Language */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              onClick={() => navigateToSection('contact')}
              className="px-8 py-2.5 rounded-full shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_25px_rgba(99,102,241,0.4)] hover:scale-105"
            >
              {t('getStarted')}
            </Button>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-1 text-white hover:text-indigo-400 transition-all duration-300 py-2 px-3"
              >
                <i className="ri-global-line"></i>
                <span className="text-sm">{t('language')}</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>
              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-40 rounded-xl bg-gray-900 border border-gray-800 shadow-xl py-2 z-50">
                  <button
                    onClick={() => changeLanguage('en')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">En</span>
                    <span>English</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('zh')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">简</span>
                    <span>简体中文</span>
                  </button>
                  {/* Fixed arrow function syntax */}
                  <button
                    onClick={() => changeLanguage('pt')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">Pt</span>
                    <span>Português</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('es')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">Es</span>
                    <span>Español</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('fr')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">Fr</span>
                    <span>Français</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('de')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">De</span>
                    <span>Deutsch</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('ja')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">日</span>
                    <span>日本語</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('ko')}
                    className="flex items-center px-4 py-2 text-sm text-white hover:bg-gray-800 transition-colors w-full text-left"
                  >
                    <span className="w-6">한</span>
                    <span>한국어</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-white"
          >
            <i className="ri-menu-line ri-lg"></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 bg-gray-900/95 backdrop-blur-md rounded-xl border border-gray-800 p-6">
            <div className="space-y-4">
              <button
                onClick={() => {
                  navigateToSection('hero');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-white hover:text-indigo-400 transition-colors py-2"
              >
                {t('home')}
              </button>
              <button
                onClick={() => {
                  navigateToSection('features');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-white hover:text-indigo-400 transition-colors py-2"
              >
                {t('features')}
              </button>
              <button
                onClick={() => {
                  navigateToSection('cases');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-white hover:text-indigo-400 transition-colors py-2"
              >
                {t('cases')}
              </button>
              <button
                onClick={() => {
                  navigateToSection('pricing');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-white hover:text-indigo-400 transition-colors py-2"
              >
                {t('pricing')}
              </button>
              <button
                onClick={() => {
                  navigateToSection('about');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-white hover:text-indigo-400 transition-colors py-2"
              >
                {t('about')}
              </button>

              <Button
                onClick={() => {
                  navigateToSection('contact');
                  setIsMenuOpen(false);
                }}
                className="w-full mt-4"
              >
                {t('getStarted')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
